import React from 'react';
import { BaseDropDown } from '../../operations/common/dropdowns';
import { TExtraRateOption } from './types';
import { CardHeader } from './shared/CardHeader';
import { EditableField } from './shared/EditableField';

type HeaderPill = { label: string; value: string | number | undefined };

type PayRateValues = {
    duration: number | string;
    payRate: number | string;
    chargeRate: number | string;
    code: string;
    description: string;
};

interface PayRateCardProps {
    id: number;
    headerPills: HeaderPill[];
    values: PayRateValues;
    isEditing: boolean;
    canEditCode: boolean;
    rateOptions?: TExtraRateOption[];
    onDurationChange: (id: number, value: string) => void;
    onPayChange: (id: number, value: string) => void;
    onChargeChange: (id: number, value: string) => void;
    onCodeChange: (id: number, value: string, name: string) => void;
    onDescriptionChange: (id: number, value: string) => void;
    onRemove?: (id: number) => void;
    codeLabel?: string;
    descriptionLabel?: string;
}

export const PayRateCard: React.FC<PayRateCardProps> = React.memo(
    ({
        id,
        headerPills,
        values,
        isEditing,
        canEditCode,
        rateOptions = [],
        onDurationChange,
        onPayChange,
        onChargeChange,
        onCodeChange,
        onDescriptionChange,
        onRemove,
        codeLabel = 'Rate Code',
        descriptionLabel = 'Description',
    }) => {
        const durationNum = Number(values.duration);
        const payNum = Number(values.payRate);
        const chargeNum = Number(values.chargeRate);

        const hasDurationError =
            values.duration === '' || Number.isNaN(durationNum) || durationNum === 0;
        const hasPayError = values.payRate === '' || Number.isNaN(payNum) || payNum === 0;
        const hasChargeError =
            values.chargeRate === '' || Number.isNaN(chargeNum) || chargeNum === 0;
        const hasCodeError = !`${values.code ?? ''}`.trim();
        const hasDescriptionError = !`${values.description ?? ''}`.trim();

        const rateCodeOptions = rateOptions.map(option => ({
            value: option.code,
            label: `${option.code} — ${option.name}`,
            name: option.name,
        }));

        const hasCurrentRate =
            !values.code ||
            rateCodeOptions.some(option => option.value === values.code);
        const selectableOptions = hasCurrentRate
            ? rateCodeOptions
            : [
                {
                    value: values.code,
                    label: values.code,
                    name: '',
                },
                ...rateCodeOptions,
            ];

        const dropdownDefault =
            selectableOptions.find(option => option.value === values.code) ||
            { value: '', label: 'Select rate code', name: '' };

        return (
            <div className="timesheet-booking-card" key={`rate-${id}`}>
                <CardHeader pills={headerPills} />
                <div className="timesheet-booking-card__body">
                    <table className="timesheet-booking-table">
                        <tbody>
                            <EditableField
                                label="Duration (hrs)"
                                value={values.duration}
                                isEditing={isEditing}
                                hasError={hasDurationError}
                                fieldType="number"
                                onChange={e => onDurationChange(id, e.target.value)}
                                ariaLabel={`Duration for rate ${values.code || id}`}
                                disableWheel={true}
                            />
                            <EditableField
                                label="Pay Rate"
                                value={values.payRate}
                                isEditing={isEditing}
                                hasError={hasPayError}
                                fieldType="currency"
                                onChange={e => onPayChange(id, e.target.value)}
                                ariaLabel={`Pay rate for ${values.code || id}`}
                                disableWheel={true}
                            />
                            <EditableField
                                label="Charge Rate"
                                value={values.chargeRate}
                                isEditing={isEditing}
                                hasError={hasChargeError}
                                fieldType="currency"
                                onChange={e => onChargeChange(id, e.target.value)}
                                ariaLabel={`Charge rate for ${values.code || id}`}
                                disableWheel={true}
                            />
                            {isEditing && canEditCode && rateCodeOptions.length ? (
                                <tr>
                                    <td className="editable-field-label">{codeLabel}</td>
                                    <td className={hasCodeError ? 'has-error' : ''}>
                                        <div className="timesheet-booking-select">
                                            <BaseDropDown
                                                filterKey={`pay-rate-code-${id}`}
                                                selected={values.code}
                                                options={selectableOptions}
                                                defaultSelection={dropdownDefault}
                                                defaultSelectionFromParam={false}
                                                onFilterChange={((_: string, selected: { value: string }) => {
                                                    const selectedValue = (selected as { value: string })?.value ?? '';
                                                    const selectedName =
                                                        selectableOptions.find(
                                                            option => option.value === selectedValue,
                                                        )?.name || '';
                                                    onCodeChange(id, selectedValue, selectedName);
                                                }) as unknown as () => void}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                <EditableField
                                    label={codeLabel}
                                    value={values.code}
                                    isEditing={isEditing}
                                    hasError={hasCodeError}
                                    fieldType="text"
                                    onChange={e => onCodeChange(id, e.target.value, '')}
                                    ariaLabel={`Rate code for ${values.code || id}`}
                                />
                            )}
                            <EditableField
                                label={descriptionLabel}
                                value={values.description}
                                isEditing={isEditing}
                                hasError={hasDescriptionError}
                                fieldType="textarea"
                                onChange={e => onDescriptionChange(id, e.target.value)}
                                ariaLabel={`Description for ${values.code || id}`}
                            />
                        </tbody>
                    </table>
                    {isEditing && onRemove && (
                        <div className="timesheet-booking-card__footer">
                            <button
                                type="button"
                                className="timesheet-modal__secondary-btn timesheet-booking-card__action"
                                onClick={() => onRemove(id)}
                            >
                                Remove rate
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

PayRateCard.displayName = 'PayRateCard';
